import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useToast } from "../components/ui/use-toast";
import * as notificationService from "../services/notificationService";
import type { Notification } from "../services/notificationService";
import { Button } from "../components/ui/button";
import { Trash2, Check, CheckCheck, Copy, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as translationService from "../services/translationService";
import * as paymentService from "../services/paymentService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../hooks/useAuth";
import type { PaymentStatus } from "../types/api";
import { Pagination } from "../components/table/DataTable";

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "deposit":
      return "💰";
    case "withdrawal":
      return "💸";
    case "transfer":
      return "🔄";
    case "order_executed":
      return "✅";
    case "order_cancelled":
      return "❌";
    case "position_closed":
      return "🔒";
    case "currency_conversion":
      return "💱";
    case "funds_locked":
      return "🔐";
    case "funds_released":
      return "🔓";
    case "admin_action":
      return "⚙️";
    default:
      return "📢";
  }
};

const getNotificationColor = (type: string) => {
  switch (type) {
    case "deposit":
    case "order_executed":
    case "position_closed":
      return "border-emerald-500/30 bg-emerald-500/10";
    case "order_cancelled":
      return "border-rose-500/30 bg-rose-500/10";
    case "transfer":
    case "currency_conversion":
      return "border-blue-500/30 bg-blue-500/10";
    case "funds_locked":
      return "border-amber-500/30 bg-amber-500/10";
    case "funds_released":
      return "border-cyan-500/30 bg-cyan-500/10";
    case "admin_action":
      return "border-purple-500/30 bg-purple-500/10";
    case "payment":
      return "border-yellow-500/30 bg-yellow-500/10";
    case "investment":
      return "border-cyan-500/30 bg-cyan-500/10";
    default:
      return "border-white/5 bg-white/5";
  }
};

/** Langue cible pour l'API /i18n/translate (DeepL). */
const backendTranslateTarget = (lang: string): "en" | "de" | "pt" => {
  if (lang.startsWith("de")) return "de";
  if (lang.startsWith("pt")) return "pt";
  return "en";
};

// Hook pour traduire une notification
const useTranslatedNotification = (notification: Notification) => {
  const { t, i18n } = useTranslation();
  const [translatedTitle, setTranslatedTitle] = useState<string | null>(null);
  const [translatedMessage, setTranslatedMessage] = useState<string | null>(
    null
  );
  const translatingRef = useRef(false);

  // Construire la traduction avec i18n
  const i18nTranslation = useMemo(() => {
    const currentLang = i18n.language;
    if (currentLang.startsWith("fr")) {
      return null;
    }

    const metadata = notification.metadata || {};

    try {
      let builtTitle: string | null = null;
      let builtMessage: string | null = null;

      switch (notification.type) {
        case "deposit":
          builtTitle = t("notificationsPage.notifications.deposit.title");
          builtMessage = t("notificationsPage.notifications.deposit.message", {
            amount: metadata.amount,
            currency: metadata.currency,
          });
          break;

        case "transfer":
          builtTitle = t("notificationsPage.notifications.transfer.title");
          const directionKey =
            metadata.direction === "main_to_trading"
              ? "messageMainToTrading"
              : "messageTradingToMain";
          builtMessage = t(
            `notificationsPage.notifications.transfer.${directionKey}`,
            {
              amount: metadata.amount,
              currency: metadata.currency,
            }
          );
          break;

        case "order_executed":
          const baseAsset = (metadata.symbol as string)?.split("/")[0] || "";
          const side = metadata.side as string;
          const orderType = metadata.orderType as string;
          const isPartial = metadata.isPartial as boolean;
          const filledQuantity = metadata.filledQuantity as number;
          const orderTotalQuantity = metadata.orderTotalQuantity as number;
          const executedPrice = metadata.executedPrice as number;

          const formatQty = (value: number) => {
            if (!Number.isFinite(value)) return "0";
            if (Math.abs(value) < 1e-10) return "0";
            return Number(value.toFixed(8))
              .toString()
              .replace(/\.?0+$/, "");
          };

          if (isPartial) {
            builtTitle = t(
              "notificationsPage.notifications.orderExecuted.titlePartial"
            );
            if (orderType === "MARKET") {
              builtMessage = t(
                "notificationsPage.notifications.orderExecuted.messageMarketPartial",
                {
                  side,
                  orderTotalQuantity: formatQty(orderTotalQuantity),
                  asset: baseAsset,
                  filledQuantity: formatQty(filledQuantity),
                }
              );
            } else {
              builtMessage = t(
                "notificationsPage.notifications.orderExecuted.messagePartial",
                {
                  side,
                  orderTotalQuantity: formatQty(orderTotalQuantity),
                  asset: baseAsset,
                  price: executedPrice,
                  filledQuantity: formatQty(filledQuantity),
                }
              );
            }
          } else {
            builtTitle = t(
              "notificationsPage.notifications.orderExecuted.title"
            );
            if (orderType === "MARKET") {
              builtMessage = t(
                "notificationsPage.notifications.orderExecuted.messageMarket",
                {
                  side,
                  quantity: formatQty(metadata.quantity as number),
                  asset: baseAsset,
                }
              );
            } else {
              builtMessage = t(
                "notificationsPage.notifications.orderExecuted.message",
                {
                  side,
                  quantity: formatQty(metadata.quantity as number),
                  asset: baseAsset,
                  price: executedPrice,
                }
              );
            }
          }
          break;

        case "order_cancelled":
          builtTitle = t(
            "notificationsPage.notifications.orderCancelled.title"
          );
          builtMessage = t(
            "notificationsPage.notifications.orderCancelled.message",
            {
              symbol: metadata.symbol,
              reason: metadata.reason,
            }
          );
          break;

        case "position_closed":
          builtTitle = t(
            "notificationsPage.notifications.positionClosed.title"
          );
          const profitLoss = metadata.profitLoss as number;
          const messageKey = profitLoss >= 0 ? "messageGain" : "messageLoss";
          builtMessage = t(
            `notificationsPage.notifications.positionClosed.${messageKey}`,
            {
              quantity: metadata.quantity,
              symbol: metadata.symbol,
              profitLoss: Math.abs(profitLoss).toFixed(2),
            }
          );
          break;

        case "currency_conversion":
          builtTitle = t(
            "notificationsPage.notifications.currencyConversion.title"
          );
          builtMessage = t(
            "notificationsPage.notifications.currencyConversion.message",
            {
              fromAmount: metadata.fromAmount,
              fromCurrency: metadata.fromCurrency,
              toAmount: metadata.toAmount,
              toCurrency: metadata.toCurrency,
              rate: metadata.rate,
            }
          );
          break;

        case "funds_locked":
          builtTitle = t("notificationsPage.notifications.fundsLocked.title");
          builtMessage = t(
            "notificationsPage.notifications.fundsLocked.message",
            {
              amount: metadata.amount,
              currency: metadata.currency,
              reason: metadata.reason,
            }
          );
          break;

        case "funds_released":
          builtTitle = t("notificationsPage.notifications.fundsReleased.title");
          builtMessage = t(
            "notificationsPage.notifications.fundsReleased.message",
            {
              amount: metadata.amount,
              currency: metadata.currency,
              reason: metadata.reason,
            }
          );
          break;

        case "admin_action":
          builtTitle = t("notificationsPage.notifications.adminAction.title");
          builtMessage = t(
            "notificationsPage.notifications.adminAction.message",
            {
              description: metadata.description || notification.message,
            }
          );
          break;

        case "investment": {
          const totalProfitLoss = Number(metadata.totalProfitLoss) ?? 0;
          const totalProfitLossPercentage =
            Number(metadata.totalProfitLossPercentage) ?? 0;
          const amountStr =
            (totalProfitLoss >= 0 ? "+" : "") +
            totalProfitLoss.toFixed(2) +
            " USD";
          const percentageStr =
            (totalProfitLossPercentage >= 0 ? "+" : "") +
            totalProfitLossPercentage.toFixed(2) +
            "%";
          builtTitle = t(
            "notificationsPage.notifications.investment.performanceUpdateTitle"
          );
          builtMessage = t(
            "notificationsPage.notifications.investment.performanceUpdateMessage",
            { amount: amountStr, percentage: percentageStr }
          );
          break;
        }
      }

      if (builtTitle && builtMessage) {
        return { title: builtTitle, message: builtMessage };
      }
    } catch (error) {
      console.error(
        "Erreur lors de la construction de la traduction i18n:",
        error
      );
    }

    return null;
  }, [
    notification.id,
    notification.type,
    notification.metadata,
    i18n.language,
    t,
  ]);

  // Utiliser la traduction i18n si disponible, sinon traduire avec DeepL
  useEffect(() => {
    const currentLang = i18n.language;

    // Réinitialiser quand la langue ou la notification change
    translatingRef.current = false;
    setTranslatedTitle(null);
    setTranslatedMessage(null);

    if (currentLang.startsWith("fr")) {
      return;
    }

    if (i18nTranslation) {
      setTranslatedTitle(i18nTranslation.title);
      setTranslatedMessage(i18nTranslation.message);
      return;
    }

    // Fallback: traduire avec DeepL
    translatingRef.current = true;
    Promise.all([
      translationService.translateText({
        text: notification.title,
        targetLang: backendTranslateTarget(currentLang),
        sourceLang: "fr",
      }),
      translationService.translateText({
        text: notification.message,
        targetLang: backendTranslateTarget(currentLang),
        sourceLang: "fr",
      }),
    ])
      .then(([title, message]) => {
        setTranslatedTitle(title);
        setTranslatedMessage(message);
      })
      .catch((error) => {
        console.error("Erreur lors de la traduction DeepL:", error);
        setTranslatedTitle(notification.title);
        setTranslatedMessage(notification.message);
      })
      .finally(() => {
        translatingRef.current = false;
      });
  }, [
    i18nTranslation,
    notification.id,
    notification.title,
    notification.message,
    i18n.language,
  ]);

  return {
    title: translatedTitle ?? notification.title,
    message: translatedMessage ?? notification.message,
  };
};

export const NotificationsPage = () => {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const notificationsPerPage = 10;
  const filteredNotifications = useMemo(() => {
    if (!searchQuery.trim()) return notifications;
    const term = searchQuery.toLowerCase();
    return notifications.filter((n) => {
      const title = (n.title || "").toLowerCase();
      const msg = (n.message || "").toLowerCase();
      return title.includes(term) || msg.includes(term);
    });
  }, [notifications, searchQuery]);
  const totalNotificationsPages = Math.max(
    1,
    Math.ceil(filteredNotifications.length / notificationsPerPage)
  );
  const paginatedNotifications = useMemo(() => {
    if (filteredNotifications.length === 0) return [];
    const start = (notificationsPage - 1) * notificationsPerPage;
    return filteredNotifications.slice(start, start + notificationsPerPage);
  }, [filteredNotifications, notificationsPage, notificationsPerPage]);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifs, count] = await Promise.all([
        notificationService.getNotifications({ limit: 100 }),
        notificationService.getUnreadCount(),
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (error) {
      console.error("Erreur lors du chargement des notifications:", error);
      toast({
        title: t("notificationsPage.toastErrorTitle"),
        description: t("notificationsPage.toastErrorLoad"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // Rafraîchir toutes les 30 secondes
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    if (notificationsPage > totalNotificationsPages) {
      setNotificationsPage(totalNotificationsPages);
    }
  }, [notificationsPage, totalNotificationsPages]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Erreur lors du marquage:", error);
      toast({
        title: t("notificationsPage.toastErrorTitle"),
        description: t("notificationsPage.toastErrorMark"),
        variant: "destructive",
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAll(true);
      await notificationService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
      toast({
        title: t("notificationsPage.toastSuccessMarkAllTitle"),
        description: t("notificationsPage.toastSuccessMarkAllDescription"),
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors du marquage:", error);
      toast({
        title: t("notificationsPage.toastErrorTitle"),
        description: t("notificationsPage.toastErrorMarkAll"),
        variant: "destructive",
      });
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      toast({
        title: t("notificationsPage.toastSuccessDeleteTitle"),
        description: t("notificationsPage.toastSuccessDeleteDescription"),
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast({
        title: t("notificationsPage.toastErrorTitle"),
        description: t("notificationsPage.toastErrorDelete"),
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("notificationsPage.justNow");
    if (diffMins < 60)
      return t("notificationsPage.minutesAgo", { count: diffMins });
    if (diffHours < 24)
      return t("notificationsPage.hoursAgo", { count: diffHours });
    if (diffDays < 7)
      return t("notificationsPage.daysAgo", { count: diffDays });
    const dateLocale = i18n.language.startsWith("fr")
      ? "fr-FR"
      : i18n.language.startsWith("de")
      ? "de-DE"
      : i18n.language.startsWith("pt")
      ? "pt-BR"
      : "en-US";
    return date.toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 dark:bg-slate-950">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              {t("notificationsPage.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-white/70">
              {unreadCount > 0
                ? t("notificationsPage.unreadSummary", {
                    count: unreadCount,
                    pluralS: unreadCount > 1 ? "s" : "",
                  })
                : t("notificationsPage.allRead")}
            </p>
            {searchQuery.trim() && (
              <p className="mt-1 text-xs text-slate-500 dark:text-white/50">
                {t("notificationsPage.searchResults", {
                  count: filteredNotifications.length,
                })}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 md:items-end md:min-w-[260px]">
            <Input
              className="h-9 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 dark:border-white/10 dark:bg-black/30 dark:text-white dark:placeholder:text-white/50"
              placeholder={t(
                "notificationsPage.searchPlaceholder",
                "Rechercher dans les notifications..."
              )}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setNotificationsPage(1);
              }}
            />
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllAsRead}
                disabled={markingAll}
                variant="outline"
                className="h-9 border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:bg-white/10">
                <CheckCheck className="mr-2 h-4 w-4" />
                {t("notificationsPage.markAllRead")}
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-slate-600 dark:text-white/70">{t("notificationsPage.loading")}</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100/80 p-12 text-center dark:border-white/5 dark:from-slate-900/60 dark:to-slate-800/40">
            <p className="text-lg text-slate-600 dark:text-white/70">
              {searchQuery.trim()
                ? t(
                    "notificationsPage.searchEmptyTitle",
                    "Aucune notification ne correspond à votre recherche."
                  )
                : t("notificationsPage.emptyTitle")}
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-white/50">
              {searchQuery.trim()
                ? t(
                    "notificationsPage.searchEmptySubtitle",
                    "Modifiez le terme de recherche ou effacez-le pour voir toutes les notifications."
                  )
                : t("notificationsPage.emptySubtitle")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
                formatDate={formatDate}
                onRefresh={loadNotifications}
              />
            ))}
            {notifications.length > notificationsPerPage && (
              <div className="mt-6 border-t border-slate-200 pt-3 dark:border-white/10">
                <Pagination
                  currentPage={notificationsPage}
                  totalPages={totalNotificationsPages}
                  totalItems={notifications.length}
                  itemsPerPage={notificationsPerPage}
                  onPageChange={setNotificationsPage}
                  isLoading={loading}
                  ariaLabelPrevious={t("notificationsPage.pagination.previous")}
                  ariaLabelNext={t("notificationsPage.pagination.next")}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour afficher une notification traduite
const NotificationItem = ({
  notification,
  onMarkAsRead,
  onDelete,
  formatDate,
  onRefresh,
}: {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
  formatDate: (dateString: string) => string;
  onRefresh: () => void;
}) => {
  const { title, message } = useTranslatedNotification(notification);
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [approving, setApproving] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(
    null
  );
  const [checkingPayment, setCheckingPayment] = useState(false);

  const isNewClientAlert =
    notification.type === "admin_action" &&
    notification.metadata?.action === "new_client_registered";

  // Fonction pour vérifier le statut du paiement
  const checkPaymentStatus = useCallback(async () => {
    if (notification.type === "payment" && notification.metadata?.paymentId) {
      try {
        setCheckingPayment(true);
        // S'assurer que paymentId est un nombre
        const paymentId =
          typeof notification.metadata.paymentId === "string"
            ? parseInt(notification.metadata.paymentId, 10)
            : Number(notification.metadata.paymentId);

        if (isNaN(paymentId) || paymentId <= 0) {
          console.warn(
            "[STATUS CHECK] paymentId invalide:",
            notification.metadata.paymentId
          );
          return;
        }

        const payment = await paymentService.getPaymentById(paymentId);
        if (payment) {
          console.log(
            "[STATUS CHECK] Statut du paiement trouvé:",
            payment.status,
            "Payment ID:",
            payment.id
          );
          setPaymentStatus(payment.status);
        } else {
          console.warn(
            "[STATUS CHECK] Paiement introuvable, paymentId:",
            paymentId,
            "Notification ID:",
            notification.id
          );
          // Si le paiement n'est pas trouvé, on garde le statut null pour ne pas afficher le bouton
        }
      } catch (error) {
        console.error(
          "[STATUS CHECK] Erreur lors de la vérification du statut du paiement:",
          error
        );
        // En cas d'erreur (404, etc.), on garde le statut null
      } finally {
        setCheckingPayment(false);
      }
    }
  }, [notification.type, notification.metadata?.paymentId, notification.id]);

  // Vérifier le statut du paiement au chargement
  useEffect(() => {
    void checkPaymentStatus();
  }, [checkPaymentStatus]);

  // Rafraîchir le statut quand les notifications sont rafraîchies
  useEffect(() => {
    if (notification.type === "payment" && notification.metadata?.paymentId) {
      void checkPaymentStatus();
    }
  }, [notification.id, checkPaymentStatus]); // Rafraîchir quand la notification change (via onRefresh)

  // Vérifier si c'est une notification de paiement nécessitant une approbation
  // Exclure les paiements annulés ou les notifications d'annulation
  // Afficher le bouton si le statut est null (en cours de chargement), "requires_action" ou "pending"
  // Ne pas afficher si le statut est "cancelled", "failed" ou "confirmed"
  const isPaymentNotification =
    notification.type === "payment" &&
    notification.metadata?.paymentId &&
    notification.metadata?.verificationCode &&
    !notification.metadata?.cancelled && // Exclure les notifications d'annulation
    (user?.role === "manager_contact" || user?.role === "manager_trader") &&
    // Vérifier le statut : afficher seulement si null (en chargement), "requires_action" ou "pending"
    // et exclure explicitement "cancelled", "failed" et "confirmed"
    (paymentStatus === null ||
      (paymentStatus !== "cancelled" &&
        paymentStatus !== "failed" &&
        paymentStatus !== "confirmed" &&
        (paymentStatus === "requires_action" || paymentStatus === "pending")));

  const isPaymentActionable =
    notification.type === "payment" &&
    notification.metadata?.paymentId &&
    !notification.metadata?.cancelled &&
    (user?.role === "manager_contact" || user?.role === "manager_trader") &&
    (paymentStatus === null ||
      paymentStatus === "pending" ||
      paymentStatus === "requires_action");

  const getPaymentStatusLabel = (status: PaymentStatus | null) => {
    if (!status) {
      return { label: "Statut inconnu", className: "bg-slate-500/20 text-slate-300" };
    }
    if (status === "pending") {
      return { label: "En attente", className: "bg-amber-500/20 text-amber-300" };
    }
    if (status === "requires_action") {
      return {
        label: "En attente de validation manager",
        className: "bg-cyan-500/20 text-cyan-300",
      };
    }
    if (status === "confirmed") {
      return { label: "Validé", className: "bg-emerald-500/20 text-emerald-300" };
    }
    if (status === "failed") {
      return { label: "Échoué", className: "bg-rose-500/20 text-rose-300" };
    }
    return { label: "Annulé", className: "bg-rose-500/20 text-rose-300" };
  };

  const resolvedTitle =
    notification.type === "payment" && paymentStatus === "cancelled"
      ? notification.metadata?.cancelledBy === "client"
        ? t(
            "notificationsPage.notifications.payment.cancelledByClientTitle",
            "Paiement annulé par le client"
          )
        : notification.metadata?.cancelledBy === "manager" ||
            notification.metadata?.cancelledBy === "admin"
        ? t(
            "notificationsPage.notifications.payment.refusedByManagerTitle",
            "Paiement refusé par le manager"
          )
        : t("notificationsPage.notifications.payment.cancelledTitle", "Paiement annulé")
      : notification.type === "payment" && paymentStatus === "confirmed"
      ? t(
          "notificationsPage.notifications.payment.confirmedTitle",
          "Paiement validé"
        )
      : notification.type === "payment" && paymentStatus === "failed"
      ? t("notificationsPage.notifications.payment.failedTitle", "Paiement échoué")
      : title;

  const resolvedMessage =
    notification.type === "payment" && paymentStatus === "cancelled"
      ? notification.metadata?.cancelledBy === "client"
        ? t(
            "notificationsPage.notifications.payment.cancelledByClientMessage",
            "Le client {{userName}} a annulé son paiement de {{amount}} {{currency}}.",
            {
              userName:
                notification.metadata?.userName ??
                t("notificationsPage.notifications.payment.theClient", "le client"),
              amount: notification.metadata?.amount ?? "",
              currency: notification.metadata?.currency ?? "",
            }
          )
        : notification.metadata?.cancelledBy === "manager" ||
            notification.metadata?.cancelledBy === "admin"
        ? user?.role === "manager_contact" || user?.role === "manager_trader"
          ? t(
              "notificationsPage.notifications.payment.refusedByYouMessage",
              "Vous avez refusé le paiement de {{amount}} {{currency}} du client {{userName}}.",
              {
                amount: notification.metadata?.amount ?? "",
                currency: notification.metadata?.currency ?? "",
                userName: notification.metadata?.userName ?? "",
              }
            )
          : t(
              "notificationsPage.notifications.payment.refusedByManagerMessage",
              "{{managerName}} a refusé votre paiement de {{amount}} {{currency}}.",
              {
                managerName:
                  notification.metadata?.cancelledByUserName ??
                  t(
                    "notificationsPage.notifications.payment.yourManager",
                    "votre manager"
                  ),
                amount: notification.metadata?.amount ?? "",
                currency: notification.metadata?.currency ?? "",
              }
            )
        : message
      : notification.type === "payment" && paymentStatus === "confirmed"
      ? t(
          "notificationsPage.notifications.payment.confirmedMessage",
          "Le paiement de {{amount}} {{currency}} a été validé.",
          {
            amount: notification.metadata?.amount ?? "",
            currency: notification.metadata?.currency ?? "",
          }
        )
      : notification.type === "payment" && paymentStatus === "failed"
      ? t(
          "notificationsPage.notifications.payment.failedMessage",
          "Le paiement de {{amount}} {{currency}} a échoué.",
          {
            amount: notification.metadata?.amount ?? "",
            currency: notification.metadata?.currency ?? "",
          }
        )
      : message;

  const handleCancelPayment = async () => {
    if (!notification.metadata?.paymentId) return;

    try {
      setApproving(true);
      await paymentService.cancelPayment(Number(notification.metadata.paymentId));
      toast({
        title: "Paiement annulé",
        description: "Le paiement a été annulé avec succès.",
        variant: "success",
      });
      setPaymentStatus("cancelled");
      setCancelConfirmOpen(false);
      onRefresh();
    } catch (error) {
      toast({
        title: t("notifications.approval.errorTitle"),
        description:
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : "Impossible d'annuler le paiement.",
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleApprovePayment = async () => {
    if (!notification.metadata?.paymentId || !confirmationCode.trim()) {
      toast({
        title: t("notifications.approval.codeRequired"),
        description: t(
          "notifications.approval.codeRequiredDescription",
          "Veuillez entrer le code de confirmation."
        ),
        variant: "destructive",
      });
      return;
    }

    // Vérifier une dernière fois le statut du paiement avant de confirmer
    // Récupérer directement le statut au lieu d'utiliser l'état
    let currentPaymentStatus: PaymentStatus | null = null;
    try {
      if (!notification.metadata?.paymentId) {
        toast({
          title: t("notifications.approval.errorTitle"),
          description: t(
            "notifications.approval.errorDescription",
            "ID de paiement manquant."
          ),
          variant: "destructive",
        });
        setApprovalModalOpen(false);
        setConfirmationCode("");
        return;
      }

      // S'assurer que paymentId est un nombre
      const paymentId =
        typeof notification.metadata.paymentId === "string"
          ? parseInt(notification.metadata.paymentId, 10)
          : Number(notification.metadata.paymentId);

      if (isNaN(paymentId) || paymentId <= 0) {
        toast({
          title: t("notifications.approval.errorTitle"),
          description: t(
            "notifications.approval.errorDescription",
            "ID de paiement invalide."
          ),
          variant: "destructive",
        });
        setApprovalModalOpen(false);
        setConfirmationCode("");
        return;
      }

      const payment = await paymentService.getPaymentById(paymentId);
      if (payment) {
        currentPaymentStatus = payment.status;
        setPaymentStatus(payment.status); // Mettre à jour l'état aussi
        console.log(
          "[APPROVAL] Statut du paiement vérifié avant confirmation:",
          currentPaymentStatus,
          "Payment ID:",
          payment.id
        );
      } else {
        console.error(
          "[APPROVAL] Paiement introuvable lors de la vérification du statut, paymentId:",
          paymentId
        );
        toast({
          title: t("notifications.approval.errorTitle"),
          description: t(
            "notifications.approval.errorDescription",
            "Paiement introuvable ou vous n'avez pas l'autorisation d'y accéder."
          ),
          variant: "destructive",
        });
        setApprovalModalOpen(false);
        setConfirmationCode("");
        onRefresh(); // Rafraîchir les notifications
        return;
      }
    } catch (error) {
      console.error(
        "[APPROVAL] Erreur lors de la vérification du statut du paiement:",
        error
      );
      toast({
        title: t("notifications.approval.errorTitle"),
        description: t(
          "notifications.approval.errorDescription",
          "Impossible d'approuver le paiement."
        ),
        variant: "destructive",
      });
      setApprovalModalOpen(false);
      setConfirmationCode("");
      return;
    }

    // Bloquer l'approbation si le paiement est annulé, échoué ou déjà confirmé
    if (
      currentPaymentStatus === "cancelled" ||
      currentPaymentStatus === "failed" ||
      currentPaymentStatus === "confirmed"
    ) {
      console.log("[APPROVAL] Paiement bloqué - statut:", currentPaymentStatus);
      toast({
        title: t("notifications.approval.errorTitle"),
        description:
          currentPaymentStatus === "cancelled"
            ? t(
                "notifications.approval.paymentCancelled",
                "Ce paiement a été annulé et ne peut plus être approuvé."
              )
            : currentPaymentStatus === "failed"
            ? t(
                "notifications.approval.paymentFailed",
                "Ce paiement a échoué et ne peut plus être approuvé."
              )
            : t(
                "notifications.approval.paymentAlreadyConfirmed",
                "Ce paiement a déjà été confirmé."
              ),
        variant: "destructive",
      });
      setApprovalModalOpen(false);
      setConfirmationCode("");
      onRefresh(); // Rafraîchir les notifications pour mettre à jour l'affichage
      return;
    }

    setApproving(true);
    try {
      await paymentService.confirmPayment(notification.metadata.paymentId, {
        confirmationValue: confirmationCode.trim(),
      });

      toast({
        title: t("notifications.approval.successTitle"),
        description: t(
          "notifications.approval.successDescription",
          "Le paiement a été approuvé avec succès. Le compte du client sera crédité."
        ),
        variant: "success",
      });

      setApprovalModalOpen(false);
      setConfirmationCode("");
      await onMarkAsRead(notification.id);
      onRefresh();
    } catch (error) {
      console.error("Erreur lors de l'approbation du paiement:", error);
      toast({
        title: t("notifications.approval.errorTitle"),
        description:
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : t(
                "notifications.approval.errorDescription",
                "Impossible d'approuver le paiement."
              ),
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  return (
    <>
      <div
        className={`rounded-xl border backdrop-blur-sm p-4 transition-all hover:shadow-lg ${
          notification.isRead
            ? "border-slate-200 bg-white opacity-90 dark:border-white/5 dark:bg-white/5 dark:opacity-75"
            : isNewClientAlert
            ? "border-red-500/60 bg-red-500/15 border-2 shadow-[0_0_25px_rgba(248,113,113,0.5)]"
            : `${getNotificationColor(notification.type)} border-2`
        }`}>
        <div className="flex items-start gap-4">
          <div className="text-2xl">
            {isNewClientAlert ? "🚨" : getNotificationIcon(notification.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  {resolvedTitle}
                </h3>
                <p className="mt-1 text-sm text-slate-700 dark:text-white/80">
                  {resolvedMessage}
                </p>
                {isPaymentNotification &&
                  notification.metadata?.verificationCode && (
                    <div className="mt-2 rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-2">
                      <p className="text-xs text-cyan-400 mb-1">
                        {t(
                          "notifications.approval.codeLabel",
                          "Code de confirmation :"
                        )}
                      </p>
                      <p className="text-sm font-mono font-bold text-cyan-300">
                        {notification.metadata.verificationCode}
                      </p>
                    </div>
                  )}
                <p className="mt-2 text-xs text-slate-500 dark:text-white/50">
                  {formatDate(notification.createdAt)}
                </p>
                {notification.type === "payment" && (
                  <div className="mt-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-[11px] font-medium ${getPaymentStatusLabel(paymentStatus).className}`}>
                      {getPaymentStatusLabel(paymentStatus).label}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {(isPaymentNotification || isPaymentActionable) && (
                  <>
                    {isPaymentNotification && (
                    <Button
                      onClick={async () => {
                      // Vérifier le statut du paiement avant d'ouvrir le modal
                      // Utiliser getPaymentById pour récupérer le paiement directement
                      if (!notification.metadata?.paymentId) {
                        toast({
                          title: t(
                            "notifications.approval.errorTitle",
                            "Erreur"
                          ),
                          description: t(
                            "notifications.approval.errorDescription",
                            "ID de paiement manquant."
                          ),
                          variant: "destructive",
                        });
                        return;
                      }

                      // S'assurer que paymentId est un nombre
                      const paymentId =
                        typeof notification.metadata.paymentId === "string"
                          ? parseInt(notification.metadata.paymentId, 10)
                          : Number(notification.metadata.paymentId);

                      if (isNaN(paymentId) || paymentId <= 0) {
                        toast({
                          title: t(
                            "notifications.approval.errorTitle",
                            "Erreur"
                          ),
                          description: t(
                            "notifications.approval.errorDescription",
                            "ID de paiement invalide."
                          ),
                          variant: "destructive",
                        });
                        return;
                      }

                      try {
                        const payment = await paymentService.getPaymentById(
                          paymentId
                        );
                        if (payment) {
                          const currentStatus = payment.status;
                          setPaymentStatus(currentStatus); // Mettre à jour l'état
                          console.log(
                            "[BUTTON CLICK] Statut du paiement:",
                            currentStatus,
                            "Payment ID:",
                            payment.id
                          );

                          // Vérifier si le paiement peut être approuvé
                          if (
                            currentStatus === "cancelled" ||
                            currentStatus === "failed" ||
                            currentStatus === "confirmed"
                          ) {
                            console.log(
                              "[BUTTON CLICK] Paiement bloqué - statut:",
                              currentStatus
                            );
                            toast({
                              title: t(
                                "notifications.approval.errorTitle",
                                "Erreur"
                              ),
                              description:
                                currentStatus === "cancelled"
                                  ? t(
                                      "notifications.approval.paymentCancelled",
                                      "Ce paiement a été annulé et ne peut plus être approuvé."
                                    )
                                  : currentStatus === "failed"
                                  ? t(
                                      "notifications.approval.paymentFailed",
                                      "Ce paiement a échoué et ne peut plus être approuvé."
                                    )
                                  : t(
                                      "notifications.approval.paymentAlreadyConfirmed",
                                      "Ce paiement a déjà été confirmé."
                                    ),
                              variant: "destructive",
                            });
                            onRefresh(); // Rafraîchir les notifications
                            return;
                          }
                          // Auto-remplir le code de confirmation si disponible
                          if (notification.metadata?.verificationCode) {
                            setConfirmationCode(
                              notification.metadata.verificationCode
                            );
                          }
                          setApprovalModalOpen(true);
                        } else {
                          console.error(
                            "[BUTTON CLICK] Paiement introuvable, paymentId:",
                            paymentId
                          );
                          toast({
                            title: t(
                              "notifications.approval.errorTitle",
                              "Erreur"
                            ),
                            description: t(
                              "notifications.approval.errorDescription",
                              "Paiement introuvable ou vous n'avez pas l'autorisation d'y accéder."
                            ),
                            variant: "destructive",
                          });
                          return;
                        }
                      } catch (error) {
                        console.error(
                          "[BUTTON CLICK] Erreur lors de la vérification du statut:",
                          error
                        );
                        toast({
                          title: t(
                            "notifications.approval.errorTitle",
                            "Erreur"
                          ),
                          description: t(
                            "notifications.approval.errorDescription",
                            "Impossible de vérifier le statut du paiement."
                          ),
                          variant: "destructive",
                        });
                        return;
                      }
                    }}
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white">
                      {t(
                        "notifications.approval.approveButton",
                        "Approuver l'achat"
                      )}
                    </Button>
                    )}
                    {isPaymentActionable && (
                      <Button
                        onClick={() => setCancelConfirmOpen(true)}
                        size="sm"
                        variant="destructive"
                        disabled={approving}
                        className="bg-rose-500 hover:bg-rose-600 text-white">
                        <XCircle className="mr-1 h-4 w-4" />
                        Annuler
                      </Button>
                    )}
                  </>
                )}
                {!notification.isRead && (
                  <Button
                    onClick={() => onMarkAsRead(notification.id)}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-slate-600 dark:text-white/70 hover:text-white">
                    <Check className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  onClick={() => onDelete(notification.id)}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-slate-600 dark:text-white/70 hover:text-rose-400">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'approbation */}
      <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-950 dark:text-white">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
              {t("notifications.approval.modalTitle")}
            </DialogTitle>
            <DialogDescription className="pt-1 text-sm text-slate-600 dark:text-white/75">
              {t(
                "notifications.approval.modalSubtitle",
                "Vérifiez les informations puis validez le paiement avec le code de confirmation."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {notification.metadata && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
                <p className="text-sm text-slate-600 dark:text-white/70">
                  {t("notifications.approval.clientInfo")}:{" "}
                  {notification.metadata.userName || "N/A"}
                </p>
                <p className="text-sm text-slate-600 dark:text-white/70">
                  {t("notifications.approval.amountInfo")}:{" "}
                  {notification.metadata.amount}{" "}
                  {notification.metadata.currency}
                </p>
                {notification.metadata.walletAddress && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-white/60 font-mono break-all">
                    {t("notifications.approval.walletInfo")}:{" "}
                    {notification.metadata.walletAddress}
                  </p>
                )}
              </div>
            )}
            {notification.metadata?.verificationCode && (
              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-cyan-400">
                    {t(
                      "notifications.approval.codeHint",
                      "Code de confirmation :"
                    )}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          notification.metadata?.verificationCode || ""
                        );
                        toast({
                          title: t("accounts.copySuccessTitle"),
                          description: t(
                            "accounts.copySuccessDescription",
                            "Code copié."
                          ),
                        });
                      } catch {
                        toast({
                          title: t("accounts.copyErrorTitle"),
                          description: t(
                            "accounts.copyErrorDescription",
                            "Impossible de copier le code."
                          ),
                          variant: "destructive",
                        });
                      }
                    }}
                    className="h-6 w-6 p-0 text-cyan-400 hover:text-cyan-300">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-2xl font-mono font-bold text-cyan-300">
                  {notification.metadata.verificationCode}
                </p>
              </div>
            )}
            <div>
              <Label className="text-slate-600 dark:text-white/70 mb-2 block">
                {t(
                  "notifications.approval.codeInputLabel",
                  "Entrez le code de confirmation"
                )}
              </Label>
              <Input
                type="text"
                placeholder={t(
                  "notifications.approval.codePlaceholder",
                  "Code de confirmation"
                )}
                value={confirmationCode}
                onChange={(e) =>
                  setConfirmationCode(e.target.value.toUpperCase())
                }
                className="border-slate-200 bg-white font-mono text-slate-900 dark:border-white/10 dark:bg-white/5 dark:text-white"
                maxLength={8}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && confirmationCode.trim()) {
                    handleApprovePayment();
                  }
                }}
              />
            </div>
            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setApprovalModalOpen(false);
                  setConfirmationCode("");
                }}
                disabled={approving}
                className="w-full text-slate-700 dark:text-white sm:flex-1">
                {t("notifications.approval.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleApprovePayment}
                disabled={approving || !confirmationCode.trim()}
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 sm:flex-1">
                {approving
                  ? t("notifications.approval.approving")
                  : t("notifications.approval.approve")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmation d'annulation */}
      <Dialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-950 dark:text-white">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
              {t("notifications.approval.cancelPaymentTitle", "Annuler le paiement")}
            </DialogTitle>
            <DialogDescription className="pt-1 text-sm leading-relaxed text-slate-600 dark:text-white/75">
              {t(
                "notifications.approval.cancelConfirm",
                "Confirmer l'annulation de ce paiement ? Cette action est définitive."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-1">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCancelConfirmOpen(false)}
                disabled={approving}
                className="w-full whitespace-normal text-slate-700 dark:text-white sm:flex-1">
                {t("notifications.approval.keepPaymentButton", "Conserver")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleCancelPayment}
                disabled={approving}
                className="w-full whitespace-normal bg-rose-500 text-white hover:bg-rose-600 sm:flex-1">
                {approving
                  ? t("notifications.approval.cancelling", "Annulation...")
                  : t("notifications.approval.confirmCancelButton", "Confirmer l'annulation")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationsPage;
